import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Modal,
  TextField,
  Typography,
} from '@mui/material';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import { updateMatter } from '../../actions/matters/updateMatter';
import type { Matter } from '../../types/matter';

interface EditMatterReferenceModalProps {
  open: boolean;
  onClose: () => void;
  matterId: string;
  currentUserRef: string | undefined;
  onSuccess: (matter: Matter) => void;
}

const MAX_LENGTH = 50;

const EditMatterReferenceModal: React.FC<EditMatterReferenceModalProps> = ({
  open,
  onClose,
  matterId,
  currentUserRef,
  onSuccess,
}) => {
  const [value, setValue] = useState(currentUserRef || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setValue(currentUserRef || '');
      setError('');
    }
  }, [open, currentUserRef]);

  const handleSave = async () => {
    setError('');
    const trimmed = value.trim();
    setSaving(true);
    try {
      const updated = await updateMatter(matterId, { userRef: trimmed });
      onSuccess(updated);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save reference.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={saving ? undefined : onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: 420 },
          bgcolor: 'var(--s2)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-md)',
          boxShadow: '0 25px 60px rgba(0,0,0,0.4)',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            bgcolor: 'var(--s1)',
            px: 3,
            py: 2.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            borderBottom: '1px solid var(--border)',
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 'var(--radius-sm)',
              bgcolor: 'color-mix(in srgb, var(--teal) 14%, transparent)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <BookmarkBorderIcon sx={{ color: 'var(--teal)', fontSize: 18 }} />
          </Box>
          <Box>
            <Typography
              sx={{ fontWeight: 600, fontSize: '1rem', color: 'var(--t1)', lineHeight: 1.3 }}
            >
              Your reference
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', color: 'var(--t3)' }}>
              Add a firm-internal reference for this matter
            </Typography>
          </Box>
        </Box>

        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>
            <Typography
              sx={{ fontSize: '0.76rem', color: 'var(--t3)', fontWeight: 500, mb: 0.75 }}
            >
              Reference
            </Typography>
            <TextField
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="e.g. ABC-123 or file number"
              fullWidth
              size="small"
              autoFocus
              inputProps={{ maxLength: MAX_LENGTH, 'data-testid': 'user-ref-input' }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !saving) handleSave();
              }}
            />
            <Typography sx={{ fontSize: '0.7rem', color: 'var(--t4)', mt: 0.5 }}>
              Leave blank to clear. Shown on the matter header and list view.
            </Typography>
          </Box>

          {error && (
            <Typography sx={{ color: 'var(--err)', fontSize: '0.78rem' }}>{error}</Typography>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 1 }}>
            <Button
              onClick={onClose}
              disabled={saving}
              sx={{
                textTransform: 'none',
                color: 'var(--t2)',
                fontSize: '0.82rem',
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={saving}
              onClick={handleSave}
              data-testid="user-ref-save"
              startIcon={saving ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : null}
              sx={{
                bgcolor: 'var(--teal)',
                color: 'var(--bg)',
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '0.82rem',
                borderRadius: 'var(--radius-sm)',
                '&:hover': { bgcolor: 'var(--teal-mid)' },
                '&.Mui-disabled': { bgcolor: 'var(--s3)', color: 'var(--t4)' },
              }}
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default EditMatterReferenceModal;
